import React from 'react';

import {
  AddressIcon,
  DocumentCopyIcon,
  EmailIcon,
  PhoneIcon,
  UserIcon,
} from '@sharedCR/coreDesignSystem/icons';
import ClickablePhone from 'app/shared/components/ClickablePhone';

import { getConditionalArray } from 'utils/data';
import { hasPropertyChanged } from 'utils/loanApplication';
import {
  getFieldPendingChangeAuthors,
  getFieldsPendingChangeAuthors,
  mergeFieldPendingChangeAuthors,
} from 'utils/pendingChangesAuthors';

const TITLE_COMPANY_ADDRESS_FIELDS = ['streetAddress', 'suite', 'city', 'state', 'zipCode'];

const getTitleCompanyIconRowsConfig = ({
  companyInfo,
  locationPendingChanges,
  locationAuthors,
}) => [
  {
    id: 'companyName',
    icon: DocumentCopyIcon,
    fieldLabel: 'Title Company Name',
    value: companyInfo.companyName,
    isModified: hasPropertyChanged(locationPendingChanges?.name),
    authors: getFieldPendingChangeAuthors(locationAuthors, 'name'),
  },
  {
    id: 'companyAddress',
    icon: AddressIcon,
    fieldLabel: 'Title Company Address',
    value: companyInfo.companyAddress,
    authors: getFieldsPendingChangeAuthors(locationAuthors, TITLE_COMPANY_ADDRESS_FIELDS),
    isModified: TITLE_COMPANY_ADDRESS_FIELDS.some(fieldName =>
      hasPropertyChanged(locationPendingChanges?.[fieldName]),
    ),
  },
];

const getCloserInfoIconRowsConfig = ({
  applicationId,
  companyInfo,
  companyPendingChanges,
  locationPendingChanges,
  companyAuthors,
  locationAuthors,
}) => [
  {
    id: 'closerName',
    icon: UserIcon,
    fieldLabel: 'Closer Name',
    value: companyInfo.closerName,
    authors: getFieldsPendingChangeAuthors(
      mergeFieldPendingChangeAuthors([companyAuthors, locationAuthors]),
      ['closerName'],
    ),
    isModified:
      hasPropertyChanged(companyPendingChanges?.closerName) ||
      hasPropertyChanged(locationPendingChanges?.closerName),
  },
  {
    id: 'closerPhone',
    icon: PhoneIcon,
    fieldLabel: 'Closer Phone',
    authors: getFieldsPendingChangeAuthors(
      mergeFieldPendingChangeAuthors([companyAuthors, locationAuthors]),
      ['closerContactPhone'],
    ),
    value: (
      <ClickablePhone
        applicationId={applicationId}
        emptyFallback=""
        value={companyInfo.closerPhone}
      />
    ),
    isModified:
      hasPropertyChanged(companyPendingChanges?.closerContactPhone) ||
      hasPropertyChanged(locationPendingChanges?.closerContactPhone),
  },
  {
    id: 'closerEmail',
    icon: EmailIcon,
    fieldLabel: 'Closer Email',
    authors: getFieldsPendingChangeAuthors(
      mergeFieldPendingChangeAuthors([companyAuthors, locationAuthors]),
      ['closerContactEmail'],
    ),
    value: companyInfo.closerEmail,
    isModified:
      hasPropertyChanged(companyPendingChanges?.closerContactEmail) ||
      hasPropertyChanged(locationPendingChanges?.closerContactEmail),
  },
];

export const getIconGroupsConfig = ({
  applicationId,
  companyInfo,
  companyPendingChanges,
  locationPendingChanges,
  companyAuthors = null,
  locationAuthors = null,
  isUnsecured = false,
}) => [
  ...getConditionalArray(!isUnsecured, () => [
    {
      id: 'titleCompany',
      label: 'Title Company:',
      rows: getTitleCompanyIconRowsConfig({
        companyInfo,
        locationPendingChanges,
        locationAuthors,
      }),
    },
    {
      id: 'closerInfo',
      label: 'Closer Information:',
      rows: getCloserInfoIconRowsConfig({
        applicationId,
        companyInfo,
        companyPendingChanges,
        locationPendingChanges,
        companyAuthors,
        locationAuthors,
      }),
    },
  ]),
];
